import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Shared polling hook. One place to fix the pinned-tab quota problem.
 *
 * A permanently pinned tab polling all night is how every free API tier gets
 * exhausted, so polling pauses while the document is hidden and refreshes on
 * becoming visible. Failures back off exponentially, which matters most for
 * CoinGecko: its 429 carries no CORS headers, so it surfaces as an opaque
 * network error and the only safe response is to slow down.
 */
export interface PollOptions {
  /** Pause while document.hidden and refresh on visible. Default true. */
  readonly pauseWhenHidden?: boolean;
  /** Back off exponentially on failure, up to maxBackoffSeconds. Default true. */
  readonly backoffOnError?: boolean;
  readonly maxBackoffSeconds?: number;
  /** Skip polling entirely (e.g. feature unconfigured). Default false. */
  readonly disabled?: boolean;
}

export interface PollState {
  /** Manually trigger a run now. Stable identity. */
  readonly refresh: () => void;
  /** Number of consecutive failures; 0 after any success. */
  readonly failureCount: number;
}

/**
 * Runs `fn` immediately, then every `seconds`. `fn` must resolve to true on
 * success and false on failure so backoff can be applied; it must never throw
 * (use fetchJson, which never rejects).
 */
export function usePolling(
  fn: () => Promise<boolean>,
  seconds: number,
  options: PollOptions = {},
): PollState {
  const {
    pauseWhenHidden = true,
    backoffOnError = true,
    maxBackoffSeconds = 300,
    disabled = false,
  } = options;

  const fnRef = useRef(fn);
  fnRef.current = fn;

  const [failureCount, setFailureCount] = useState(0);
  const failureRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);
  const runningRef = useRef(false);
  const [manualTick, setManualTick] = useState(0);

  const refresh = useCallback(() => setManualTick((n) => n + 1), []);

  useEffect(() => {
    if (disabled) return;
    let cancelled = false;

    const clear = (): void => {
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };

    const delayForNextRun = (): number => {
      if (!backoffOnError || failureRef.current === 0) return seconds;
      const backoff = seconds * Math.pow(2, Math.min(failureRef.current, 6));
      return Math.min(backoff, maxBackoffSeconds);
    };

    const schedule = (): void => {
      clear();
      timerRef.current = window.setTimeout(() => void run(), delayForNextRun() * 1000);
    };

    const run = async (): Promise<void> => {
      // Module-level guard: React 18 StrictMode double-invokes effects in dev,
      // and a 5-request burst limit turns that into an immediate rate-limit.
      if (runningRef.current || cancelled) return;
      if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) {
        schedule();
        return;
      }
      runningRef.current = true;
      let succeeded = false;
      try {
        succeeded = await fnRef.current();
      } finally {
        runningRef.current = false;
      }
      if (cancelled) return;
      failureRef.current = succeeded ? 0 : failureRef.current + 1;
      setFailureCount(failureRef.current);
      schedule();
    };

    void run();

    const onVisible = (): void => {
      if (typeof document !== 'undefined' && !document.hidden) void run();
    };
    if (pauseWhenHidden && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      cancelled = true;
      clear();
      if (pauseWhenHidden && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [seconds, pauseWhenHidden, backoffOnError, maxBackoffSeconds, disabled, manualTick]);

  return { refresh, failureCount };
}
